import { useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@mezzodevelopment/mezzo-ui/auth';
import { SelectButton } from 'primereact/selectbutton';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Avatar } from 'primereact/avatar';
import { Tag } from 'primereact/tag';
import { Dialog } from 'primereact/dialog';
import { ProgressSpinner } from 'primereact/progressspinner';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';
import { classNames } from 'primereact/utils';
import { formatDistanceToNow } from 'date-fns';
import { AppCard, AppPage } from '../../components/general';
import { Constants } from '../../utils/constants';
import {
  useCloseSupportTicket,
  useInfiniteSupportTickets,
  useReopenSupportTicket,
} from '../../hooks/useSupportTickets';
import { useLinearCustomerMapping } from '../../hooks/useLinearCustomerMapping';
import { SupportTicket, SupportTicketStatusFilter } from '../../models/supportTickets';
import {
  downloadTicketsCsv,
  getInitials,
  isClosedStatus,
  priorityDotClass,
  statusDotClass,
  statusSeverity,
} from './supportTicketHelpers';
import NewTicketDialog from './NewTicketDialog';
import TicketDetailDialog from './TicketDetailDialog';
import SupportPortalConfig from './SupportPortalConfig';

const statusOptions: { label: string; value: SupportTicketStatusFilter }[] = [
  { label: 'Outstanding', value: 'Outstanding' },
  { label: 'Closed', value: 'Closed' },
];

const SupportTickets = (): JSX.Element => {
  const [status, setStatus] = useState<SupportTicketStatusFilter>('Outstanding');
  const [search, setSearch] = useState('');
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [selectedIssueId, setSelectedIssueId] = useState<string | undefined>(undefined);

  const { user } = useAuth();
  const isSuperAdmin = (user?.roles || []).includes('SuperAdmin');
  // Use the same tenant id the API client sends as X-TenantId so the config
  // panel targets the org whose support portal we are configuring.
  const tenantId = localStorage.getItem(Constants.authorization.tenantId) || undefined;
  const { data: mapping, isFetched: mappingFetched } = useLinearCustomerMapping(
    isSuperAdmin ? tenantId : undefined
  );

  // A super admin can read the mapping directly, so gate the ticket request on an active
  // mapping rather than firing it and relying on a 403 to discover support is not connected.
  // Regular users cannot read the mapping, so for them the request stays enabled and a 403
  // is the signal that support is unavailable.
  const ticketsEnabled = isSuperAdmin ? mapping?.status === 'Active' : true;

  const {
    data,
    isPending,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteSupportTickets(status, ticketsEnabled);

  const closeMutation = useCloseSupportTicket();
  const reopenMutation = useReopenSupportTicket();

  const runClose = (issueId: string) => {
    confirmDialog({
      group: 'support-list',
      message: 'Close this ticket? You can reopen it later if needed.',
      header: 'Close Ticket',
      icon: 'pi pi-exclamation-triangle',
      accept: () => closeMutation.mutate(issueId),
    });
  };

  const runReopen = (issueId: string) => {
    confirmDialog({
      group: 'support-list',
      message: 'Reopen this ticket?',
      header: 'Reopen Ticket',
      icon: 'pi pi-replay',
      accept: () => reopenMutation.mutate(issueId),
    });
  };

  const tickets: SupportTicket[] = useMemo(
    () => data?.pages.flatMap((page) => page.tickets) ?? [],
    [data]
  );

  const filteredTickets = useMemo(() => {
    const term = search.trim().toLowerCase();
    const rows = term
      ? tickets.filter(
          (ticket) =>
            ticket.title.toLowerCase().includes(term) ||
            ticket.identifier.toLowerCase().includes(term) ||
            (ticket.assigneeName ?? '').toLowerCase().includes(term)
        )
      : tickets;
    return [...rows].sort(
      (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
    );
  }, [tickets, search]);

  // Group rows under their status, preserving the updatedAt ordering: the first time a
  // status appears fixes that group's position, so the most recently touched statuses
  // surface first, matching the reference layout's status sections.
  const groupedTickets = useMemo(() => {
    const groups = new Map<string, SupportTicket[]>();
    filteredTickets.forEach((ticket) => {
      const status = ticket.status || 'Other';
      const existing = groups.get(status);
      if (existing) existing.push(ticket);
      else groups.set(status, [ticket]);
    });
    return Array.from(groups, ([status, items]) => ({ status, items }));
  }, [filteredTickets]);

  // For a super admin, support is unavailable whenever the mapping is not active. A missing tenant
  // id disables the mapping query (so it never resolves) and also means there is no org to connect,
  // so treat it as unavailable too rather than spinning forever. For everyone else it is signalled
  // by a 403 from the ticket request. When the super admin later connects a Customer the mapping
  // flips to Active, which re-enables the ticket query automatically.
  const isUnavailable = isSuperAdmin
    ? !tenantId || (mappingFetched && mapping?.status !== 'Active')
    : isError && axios.isAxiosError(error) && error.response?.status === 403;

  const headerEnd = isUnavailable ? null : (
    <div className="flex items-center gap-2">
      {isSuperAdmin && (
        <Button
          label="Support config"
          icon="pi pi-cog"
          outlined
          onClick={() => setShowConfigDialog(true)}
        />
      )}
      <Button
        label="Export CSV"
        icon="pi pi-download"
        outlined
        onClick={() => downloadTicketsCsv(filteredTickets)}
        disabled={filteredTickets.length === 0}
      />
      <Button label="New Ticket" icon="pi pi-plus" onClick={() => setShowNewDialog(true)} />
    </div>
  );

  const renderTicketRow = (ticket: SupportTicket) => {
    const closed = isClosedStatus(ticket.status);
    const relativeUpdated = formatDistanceToNow(new Date(ticket.updatedAt), { addSuffix: true });
    const subtitle = [(ticket.labels ?? []).join(' \u00b7 '), relativeUpdated]
      .filter(Boolean)
      .join(' \u00b7 ');

    return (
      <div
        key={ticket.id}
        role="button"
        tabIndex={0}
        onClick={() => setSelectedIssueId(ticket.id)}
        onKeyDown={(e) => {
          if (e.target !== e.currentTarget) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setSelectedIssueId(ticket.id);
          }
        }}
        className="flex items-center gap-4 rounded-xl border border-gray-100 bg-white px-4 py-3 shadow-sm cursor-pointer transition hover:border-gray-200 hover:shadow"
      >
        <span className="w-24 shrink-0 text-sm font-medium text-[#2571ab]">{ticket.identifier}</span>
        <div className="min-w-0 flex-1">
          <div className="truncate font-medium text-gray-800">{ticket.title}</div>
          {subtitle && <div className="mt-0.5 truncate text-xs text-gray-400">{subtitle}</div>}
        </div>
        <div className="hidden w-24 shrink-0 items-center gap-1.5 text-sm text-gray-600 md:flex">
          <span
            className={classNames('inline-block shrink-0 rounded-full', priorityDotClass(ticket.priority))}
            style={{ width: '0.5rem', height: '0.5rem' }}
          />
          <span className="truncate capitalize">{ticket.priority}</span>
        </div>
        <div className="hidden w-44 shrink-0 items-center gap-2 md:flex">
          {ticket.assigneeName ? (
            <>
              <Avatar
                label={getInitials(ticket.assigneeName)}
                shape="circle"
                className="bg-[#0a1f3d] text-[0.7rem] text-white"
                style={{ width: '28px', height: '28px' }}
              />
              <span className="truncate text-sm text-gray-700">{ticket.assigneeName}</span>
            </>
          ) : (
            <span className="text-sm text-gray-400">Unassigned</span>
          )}
        </div>
        <div className="w-28 shrink-0">
          <Tag value={ticket.status} severity={statusSeverity(ticket.status)} />
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {closed ? (
            <Button
              label="Reopen"
              icon="pi pi-replay"
              outlined
              size="small"
              loading={reopenMutation.isPending && reopenMutation.variables === ticket.id}
              onClick={(e) => {
                e.stopPropagation();
                runReopen(ticket.id);
              }}
            />
          ) : (
            <>
              <Button
                label="Respond"
                outlined
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedIssueId(ticket.id);
                }}
              />
              <Button
                label="Confirm & close"
                outlined
                size="small"
                severity="secondary"
                loading={closeMutation.isPending && closeMutation.variables === ticket.id}
                onClick={(e) => {
                  e.stopPropagation();
                  runClose(ticket.id);
                }}
              />
            </>
          )}
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (isUnavailable) {
      return (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <i className="pi pi-lock text-3xl text-gray-300 mb-3" />
          <div className="text-gray-600 font-medium">Support is not enabled for your organization.</div>
          <div className="text-gray-400 text-sm mt-1">
            Contact your Mezzo representative to enable support tickets.
          </div>
          {isSuperAdmin && (
            <Button
              label="Configure support"
              icon="pi pi-cog"
              className="mt-4"
              onClick={() => setShowConfigDialog(true)}
            />
          )}
        </div>
      );
    }

    if (isError) {
      return (
        <div className="text-red-600 text-sm py-8 text-center">
          {error instanceof Error ? error.message : 'Failed to load support tickets.'}
        </div>
      );
    }

    if (isPending) {
      return (
        <div className="flex justify-center py-16">
          <ProgressSpinner style={{ width: '2.5rem', height: '2.5rem' }} />
        </div>
      );
    }

    return (
      <>
        {filteredTickets.length === 0 ? (
          <div className="py-16 text-center text-sm text-gray-400">No tickets found.</div>
        ) : (
          <div className="flex flex-col gap-6">
            {groupedTickets.map((group) => (
              <div key={group.status} className="flex flex-col gap-2">
                <div className="flex items-center gap-2 px-1">
                  <span
                    className={classNames('inline-block shrink-0 rounded-full', statusDotClass(group.status))}
                    style={{ width: '0.5rem', height: '0.5rem' }}
                  />
                  <span className="text-sm font-semibold text-gray-700">{group.status}</span>
                  <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">
                    {group.items.length}
                  </span>
                </div>
                {group.items.map(renderTicketRow)}
              </div>
            ))}
          </div>
        )}

        {hasNextPage && (
          <div className="flex justify-center mt-4">
            <Button
              label="Load more"
              icon="pi pi-angle-down"
              text
              onClick={() => fetchNextPage()}
              loading={isFetchingNextPage}
            />
          </div>
        )}
      </>
    );
  };

  return (
    <AppPage title="Support Tickets" breadcrumbs="Support" headerEnd={headerEnd}>
      <ConfirmDialog group="support-list" />
      <AppCard>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <SelectButton
            value={status}
            onChange={(e) => e.value && setStatus(e.value)}
            options={statusOptions}
            allowEmpty={false}
          />
          <span className="p-input-icon-left">
            <i className="pi pi-search" />
            <InputText
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search title, ID, or assignee"
            />
          </span>
        </div>

        {renderContent()}
      </AppCard>

      <NewTicketDialog
        visible={showNewDialog}
        onHide={() => setShowNewDialog(false)}
        onCreated={(ticket) => setSelectedIssueId(ticket.id)}
      />

      <TicketDetailDialog
        key={selectedIssueId}
        issueId={selectedIssueId}
        onHide={() => setSelectedIssueId(undefined)}
      />

      <Dialog
        header="Support portal configuration"
        visible={showConfigDialog}
        onHide={() => setShowConfigDialog(false)}
        modal
        dismissableMask
        appendTo={document.body}
        style={{ width: '32rem' }}
        breakpoints={{ '960px': '90vw' }}
        footer={
          <div className="flex justify-end">
            <Button label="Close" text onClick={() => setShowConfigDialog(false)} />
          </div>
        }
      >
        <SupportPortalConfig tenantId={tenantId} />
      </Dialog>
    </AppPage>
  );
};

export default SupportTickets;
